import React from 'react';

import { Avatar, Button, Card, Flexbox, FontAwesomeIcon, Text } from '@schoolinks/ui-components';
import cn from 'classnames';
import { sumBy } from 'lodash';
import { bool, number } from 'prop-types';

import { useTranslation } from 'react-i18next';

import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faAward } from '@fortawesome/pro-solid-svg-icons/faAward';
import { faDiploma } from '@fortawesome/pro-solid-svg-icons/faDiploma';
import { faUserCircle } from '@fortawesome/pro-solid-svg-icons/faUserCircle';

import TextLink from 'components/Common/TextLink';
import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import useModal from 'hooks/useModal';
import useMutation from 'hooks/useMutation';
import {
  PROFILE_EDIT,
  STUDENT_PROFILE_DETAILS,
  RECORD_REQUESTS_TRANSCRIPT,
} from 'routes/constants';
import { ensureArray } from 'utils/arrays';
import getFullName from 'utils/getFullName';
import { ensureObject } from 'utils/objects';

import s from './OverviewCard.css';

const OverviewCard = ({ studentId, isPublic }) => {
  const { t } = useTranslation();
  const { data: { pk } = {} } = useFetch({
    apiFunc: Api.studentProfile.getStudentUserId,
    apiFuncName: 'studentProfile.getStudentUserId',
    filters: { studentId },
    options: {
      enabled: Boolean(isPublic),
    },
  });

  const { data: user } = useFetch({
    apiFunc: Api.clr.getPublicUserInfo,
    apiFuncName: 'clr.getPublicUserInfo',
    apiFuncParams: isPublic ? { userId: pk } : undefined,
    options: {
      enabled: !isPublic || Boolean(pk),
    },
  });

  const fullName = getFullName(user);
  const {
    picture: profilePic,
    classYear,
    schoolName,
    isProfileAccessible,
  } = ensureObject(user);
  const { showModal, hideModal } = useModal();

  const { data: counts } = useFetch({
    apiFunc: Api.clr.getCredentialCounts,
    apiFuncName: 'clr.getCredentialCounts',
    filters: { student: studentId },
  });

  const { mutate: exportStudentClr } = useMutation({
    apiFunc: Api.clr.exportStudentClr,
    apiFuncName: 'clr.exportStudentClr',
    successSnackbarMessage: t(
      'CredentialWallet.OverviewCard.ExportSuccessMessage',
    ),
    errorSnackbarMessage: t('CredentialWallet.OverviewCard.ExportErrorMessage'),
  });

  const badgesCount = ensureArray(counts).find(
    o => o.achievementType === 'badge',
  )?.count;
  const credentialsCount = sumBy(
    ensureArray(counts).filter(
      o => o.achievementType !== 'badge' && o.achievementType !== 'course',
    ),
    'count',
  );
  const allCount = sumBy(ensureArray(counts), 'count');

  const getStatBox = ({ containerClass, starClass, icon, subtitle, count }) => (
    <Flexbox
      flex={1}
      p={1}
      flexDirection="column"
      alignItems="center"
      rowGap={0.5}
      className={cn(s.statContainer, containerClass)}
    >
      <Flexbox columnGap={1} alignItems="center">
        <Flexbox
          justifyContent="center"
          alignItems="center"
          className={cn(s.star, starClass)}
        >
          <FontAwesomeIcon icon={icon} color="white" size="md" />
        </Flexbox>
        <Text variant='bodyReg500'>{count}</Text>
      </Flexbox>
      <Text color="grayDark" variant='captionReg700'>
        {subtitle}
      </Text>
    </Flexbox>
  );

  return (
    <Card>
      <Flexbox p={2} rowGap={3} flexDirection="column">
        <Flexbox>
          <Flexbox columnGap={1.5}>
            <Avatar alt={fullName} size='lg' src={profilePic} />
            <Flexbox flexDirection="column" justifyContent="center">
              {isProfileAccessible ? (
                <TextLink
                  variant="h4"
                  color="blueDark"
                  href={
                    isPublic
                      ? STUDENT_PROFILE_DETAILS({ studentId })
                      : PROFILE_EDIT
                  }
                >
                  {fullName}&nbsp;
                  <FontAwesomeIcon
                    icon={faUserCircle}
                    color="blueDark"
                    size="sm"
                  />
                </TextLink>
              ) : (
                <Text color="font" variant='h4'>
                  {fullName}
                </Text>
              )}
              <Text variant='bodyReg400'>
                {t('CredentialWallet.OverviewCard.ClassOf')} {classYear} |{' '}
                {schoolName}
              </Text>
            </Flexbox>
          </Flexbox>
        </Flexbox>
        <Flexbox width="100%" columnGap={1}>
          {getStatBox({
            containerClass: s.violet,
            starClass: s.violetStar,
            icon: faDiploma,
            subtitle: t('CredentialWallet.OverviewCard.Credentials'),
            count: credentialsCount,
          })}
          {getStatBox({
            containerClass: s.teal,
            starClass: s.tealStar,
            icon: faAward,
            subtitle: t('CredentialWallet.OverviewCard.Badges'),
            count: badgesCount,
          })}
        </Flexbox>
        {!isPublic && (
          <Flexbox flexDirection="column" rowGap={1.5}>
            <Flexbox
              className={cn(s.actionContainer, s.offsetButton)}
              columnGap={2}
              p={1.5}
              alignItems="center"
            >
              <div>
                <Text variant='titleReg700'>
                  {t('CredentialWallet.OverviewCard.AlreadyHaveCredentials')}
                </Text>
                <Text variant='bodySm400'>
                  {t(
                    'CredentialWallet.OverviewCard.AddCredentialsFromOtherSou',
                  )}
                </Text>
              </div>
              <div>
                <Button
                  compactVariant="default"
                  hasIconLeft
                  icon="plus"
                  label={t('CredentialWallet.OverviewCard.Add')}
                  onClick={() =>
                    showModal('ADD_CREDENTIAL_MODAL', {
                      onClose: hideModal,
                    })
                  }
                  variant="filled"
                />
              </div>
            </Flexbox>
            <Flexbox
              className={s.actionContainer}
              columnGap={2}
              p={1.5}
              alignItems="center"
            >
              <div>
                <Text variant='titleReg700'>
                  {t('CredentialWallet.OverviewCard.NeedAnOfficialCLR')}
                </Text>
                <Text variant='bodySm400'>
                  <span>{t('CredentialWallet.OverviewCard.GetA')}</span>{' '}
                  <TextLink
                    variant="bodySmBold"
                    href="https://www.1edtech.org/initiatives/digital-credentials/clr"
                  >
                    {t(
                      'CredentialWallet.OverviewCard.ComprehensiveLearnerRecord',
                    )}
                  </TextLink>
                  <span>
                    {t(
                      'CredentialWallet.OverviewCard.ARepresentationOfYourCou',
                    )}
                  </span>
                </Text>
              </div>
              <div>
                <Button
                  compactVariant="default"
                  disabled={allCount === 0}
                  hasIconLeft
                  icon={<FontAwesomeIcon icon={faArrowDownToLine} />}
                  label={t('CredentialWallet.OverviewCard.Export')}
                  onClick={() => {
                    exportStudentClr({
                      data: {
                        student: studentId,
                      },
                    });
                  }}
                  variant="filled"
                />
              </div>
            </Flexbox>
            {/* <Flexbox
             className={s.actionContainer}
             columnGap={2}
             p={1.5}
             alignItems="center"
            >
             <div>
               <Text variant="titleMdBold">Share to other wallets?</Text>
               <Text variant="bodySm">
                 Export your wallet to transfer credentials to another wallet.
               </Text>
             </div>
             <div>
               <Button label="Export" variant="filled" />
             </div>
            </Flexbox> */}
            <Text variant='bodySm400'>
              <span>
                {t('CredentialWallet.OverviewCard.NeedAnUnofficialTranscript')}
              </span>{' '}
              <TextLink variant="bodySmBold" href={RECORD_REQUESTS_TRANSCRIPT}>
                {t('CredentialWallet.OverviewCard.RecordCenter')}
              </TextLink>
              <span>.</span>
            </Text>
          </Flexbox>
        )}
      </Flexbox>
    </Card>
  );
};

OverviewCard.propTypes = {
  studentId: number,
  isPublic: bool,
};

OverviewCard.defaultProps = {
  studentId: undefined,
  isPublic: false,
};

export default OverviewCard;
